<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import TeletelMetaGuideAPI from 'dashboard/api/teletel/metaGuide';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';

const { t } = useI18n();

const loading = ref(true);
const error = ref('');
const html = ref('');
const article = ref(null);
const objectUrls = [];

// The guide HTML is a static file shipped with the code (custom/guides), not
// user input, so rendering it with v-html is safe. Every <img data-guide-img>
// is filled after mount through the authenticated image endpoint.
const loadImages = async () => {
  await nextTick();
  const nodes = article.value?.querySelectorAll('img[data-guide-img]') || [];
  await Promise.all(
    Array.from(nodes).map(async img => {
      try {
        const { data } = await TeletelMetaGuideAPI.image(img.dataset.guideImg);
        const url = URL.createObjectURL(data);
        objectUrls.push(url);
        img.src = url;
      } catch (e) {
        img.alt = t('TELETEL_META_GUIDE.IMAGE_ERROR');
      }
    })
  );
};

onMounted(async () => {
  try {
    const { data } = await TeletelMetaGuideAPI.show();
    html.value = data.html;
  } catch (e) {
    error.value =
      e?.response?.status === 403
        ? t('TELETEL_META_GUIDE.FORBIDDEN')
        : t('TELETEL_META_GUIDE.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
  // The article only mounts once loading is off, so the images are filled
  // after that flip - never before.
  if (html.value) await loadImages();
});

onBeforeUnmount(() => {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
});
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_META_GUIDE.TITLE')"
      :description="t('TELETEL_META_GUIDE.DESCRIPTION')"
    />

    <div v-if="loading" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('TELETEL_META_GUIDE.LOADING') }}
    </div>
    <div v-else-if="error" class="py-8 text-sm text-center text-n-ruby-11">
      {{ error }}
    </div>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <article
      v-else
      ref="article"
      dir="rtl"
      class="teletel-meta-guide max-w-5xl"
      v-html="html"
    />
  </div>
</template>

<style lang="scss">
.teletel-meta-guide {
  color: rgb(var(--slate-12));
  line-height: 1.8;
  font-size: 0.95rem;

  h2 {
    margin: 2.25rem 0 0.75rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid rgb(var(--slate-6));
    font-size: 1.25rem;
    font-weight: 700;
  }

  h3 {
    margin: 1.5rem 0 0.5rem;
    font-size: 1.05rem;
    font-weight: 600;
  }

  p,
  li {
    margin-bottom: 0.5rem;
  }

  ol,
  ul {
    padding-inline-start: 1.5rem;
    margin-bottom: 0.75rem;
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }

  code {
    direction: ltr;
    unicode-bidi: embed;
    padding: 0.05rem 0.35rem;
    border-radius: 0.3rem;
    background: rgb(var(--slate-3));
    font-size: 0.85em;
    word-break: break-all;
  }

  pre {
    direction: ltr;
    text-align: left;
    white-space: pre-wrap;
    padding: 0.75rem 1rem;
    margin: 0.5rem 0 1rem;
    border-radius: 0.5rem;
    background: rgb(var(--slate-3));
    font-size: 0.82rem;
    line-height: 1.55;
  }

  table {
    width: 100%;
    margin: 0.5rem 0 1rem;
    border-collapse: collapse;
    font-size: 0.9rem;
  }

  th,
  td {
    padding: 0.45rem 0.6rem;
    border: 1px solid rgb(var(--slate-6));
    vertical-align: top;
  }

  th {
    background: rgb(var(--slate-3));
    font-weight: 600;
  }

  figure {
    margin: 0.75rem 0 1.5rem;
  }

  figure img {
    display: block;
    width: 100%;
    min-height: 6rem;
    border: 1px solid rgb(var(--slate-6));
    border-radius: 0.5rem;
    background: rgb(var(--slate-2));
  }

  figcaption {
    margin-top: 0.35rem;
    font-size: 0.82rem;
    color: rgb(var(--slate-11));
  }

  .note,
  .warn,
  .stop {
    padding: 0.75rem 1rem;
    margin: 0.75rem 0;
    border-radius: 0.5rem;
    border-inline-start: 4px solid;
  }

  .note {
    background: rgb(var(--blue-2));
    border-color: rgb(var(--blue-9));
  }

  .warn {
    background: rgb(var(--amber-2));
    border-color: rgb(var(--amber-9));
  }

  .stop {
    background: rgb(var(--ruby-2));
    border-color: rgb(var(--ruby-9));
  }

  .toc {
    padding: 0.75rem 1rem;
    margin: 0 0 1rem;
    border-radius: 0.5rem;
    background: rgb(var(--slate-2));

    ol {
      margin: 0;
    }
  }
}
</style>
